import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  PauseCircle,
  PlayCircle,
  Users,
  MessageSquare,
  Bot,
  Wallet,
  Share2,
  ExternalLink,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Overview {
  grafton_product: {
    status: string;
    message: string;
    hubspot_connected: boolean;
    hubspot_portal?: number;
    total_contacts: number;
    pipeline: Record<string, number>;
  };
  activite_grafton_ia: {
    total_conversations: number;
    total_missions_agents: number;
    prospects_enrolled_nanocorp: number;
  };
  portage_itg: {
    status: string;
    message: string;
    nb_clients: number;
    ca_realise: number;
    montant_contrats: number;
  };
  tresorerie: { ca_realise_total: number };
  reseaux_sociaux: { total_followers: number };
}

const fmtEur = (n: number) =>
  n.toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

export default function MonBusiness() {
  const navigate = useNavigate();
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/mon-business/overview`, { credentials: "include" });
        if (res.ok) setData(await res.json());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Impossible de charger la vue Mon Business.
      </div>
    );
  }

  const { grafton_product, activite_grafton_ia, portage_itg, reseaux_sociaux } = data;
  const pipelineTotal =
    grafton_product.pipeline.prospect +
    grafton_product.pipeline.qualifie +
    grafton_product.pipeline.negociation +
    grafton_product.pipeline.client;

  return (
    <div className="min-h-screen pb-24">
      <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border/50 bg-background/95 px-4 py-3 backdrop-blur">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15">
          <Users className="h-4 w-4 text-primary" />
        </div>
        <div>
          <h1 className="text-lg font-bold leading-tight">Mon Business</h1>
          <p className="text-xs text-muted-foreground">Pilotage personnel de Joël — Grafton IA</p>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {/* Grafton produit — statut pause */}
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <PauseCircle className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-base">Produit Grafton IA — Développement client</CardTitle>
              </div>
              <Badge variant="outline" className="border-amber-500/40 text-amber-500">
                En pause
              </Badge>
            </div>
            <CardDescription className="pt-1">{grafton_product.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border border-border/50 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Contacts HubSpot</p>
                <p className="text-xl font-bold">{grafton_product.total_contacts}</p>
              </div>
              <div className="rounded-lg border border-border/50 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Prospects</p>
                <p className="text-xl font-bold">{grafton_product.pipeline.prospect}</p>
              </div>
              <div className="rounded-lg border border-border/50 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Qualifiés</p>
                <p className="text-xl font-bold">{grafton_product.pipeline.qualifie}</p>
              </div>
              <div className="rounded-lg border border-border/50 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Négociation</p>
                <p className="text-xl font-bold">{grafton_product.pipeline.negociation}</p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <span>
                Pipeline total actif : <strong className="text-foreground">{pipelineTotal}</strong> contacts en cours de qualification — prêt à être réactivé.
              </span>
              <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => navigate("/crm")}>
                Voir CRM <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Portage ITG — statut actif */}
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <PlayCircle className="h-5 w-5 text-emerald-500" />
                <CardTitle className="text-base">Activité Consultant — Portage salarial ITG</CardTitle>
              </div>
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-500">
                Actif
              </Badge>
            </div>
            <CardDescription className="pt-1">{portage_itg.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-border/50 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Clients</p>
                <p className="text-xl font-bold">{portage_itg.nb_clients}</p>
              </div>
              <div className="rounded-lg border border-border/50 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">CA réalisé</p>
                <p className="text-xl font-bold text-emerald-500">{fmtEur(portage_itg.ca_realise)}</p>
              </div>
              <div className="rounded-lg border border-border/50 p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Contrats signés</p>
                <p className="text-xl font-bold">{fmtEur(portage_itg.montant_contrats)}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full gap-1.5 text-xs"
              onClick={() => navigate("/portage-clients")}
            >
              Voir le détail des clients en portage <ExternalLink className="h-3 w-3" />
            </Button>
          </CardContent>
        </Card>

        {/* Activité Grafton IA */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">Activité Grafton IA</CardTitle>
            </div>
            <CardDescription>Agents, conversations, et engagement</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-border/50 p-3 text-center">
                <MessageSquare className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
                <p className="text-xl font-bold">{activite_grafton_ia.total_conversations}</p>
                <p className="text-[10px] text-muted-foreground">Conversations IA</p>
              </div>
              <div className="rounded-lg border border-border/50 p-3 text-center">
                <Bot className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
                <p className="text-xl font-bold">{activite_grafton_ia.total_missions_agents}</p>
                <p className="text-[10px] text-muted-foreground">Missions agents NanoCorp</p>
              </div>
              <div className="rounded-lg border border-border/50 p-3 text-center">
                <Users className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
                <p className="text-xl font-bold">{activite_grafton_ia.prospects_enrolled_nanocorp}</p>
                <p className="text-[10px] text-muted-foreground">Prospects suivis par les agents</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Résumé global */}
        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15">
                <Wallet className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-[10px] uppercase text-muted-foreground">CA réalisé total 2026</p>
                <p className="text-lg font-bold">{fmtEur(data.tresorerie.ca_realise_total)}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15">
                <Share2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-[10px] uppercase text-muted-foreground">Abonnés réseaux sociaux</p>
                <p className="text-lg font-bold">{reseaux_sociaux.total_followers.toLocaleString("fr-FR")}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
